import { useSyncExternalStore } from 'react'
import { adminApi, adminLongRunningApi, formatApiError } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { scopeQuery } from '../constants/knowledgeScope'

export const SUPPORTED_UPLOAD_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'rtf',
  'xls',
  'xlsx',
  'pptx',
  'md',
  'markdown',
  'png',
  'jpg',
  'jpeg',
  'webp',
  'tif',
  'tiff',
  'bmp',
])

export const UPLOAD_ACCEPT =
  '.pdf,.doc,.docx,.rtf,.xls,.xlsx,.pptx,.md,.markdown,.png,.jpg,.jpeg,.webp,.tif,.tiff,.bmp'

export type UploadTaskStatus = 'pending' | 'uploading' | 'done' | 'error'

export type UploadTask = {
  id: string
  batchId: string
  batchLabel: string
  file: File
  displayName: string
  relativePath?: string
  targetFolderId: number
  scope: KnowledgeScope
  status: UploadTaskStatus
  percent: number
  error?: string
  createdAt: number
  finishedAt?: number
}

export type UploadBatchMeta = {
  id: string
  scope: KnowledgeScope
  baseFolderId: number
  label: string
  createdAt: number
}

export type UploadOptions = {
  category?: string
  entity_type?: string
  tags?: string
  use_ocr?: boolean
  chunk_strategy?: string
}

type FolderNode = { id: number; name: string; parent_id: number | null }

const MAX_CONCURRENT = 3

let tasks: UploadTask[] = []
const batches = new Map<string, UploadBatchMeta>()
const optionsByBatch = new Map<string, UploadOptions>()
const completeCallbacks = new Map<string, () => void>()
let activeCount = 0
const listeners = new Set<() => void>()

export type UploadBatchCompleteMeta = {
  batchId: string
  scope: KnowledgeScope
  baseFolderId: number
  done: number
  failed: number
}

const batchCompleteListeners = new Set<(meta: UploadBatchCompleteMeta) => void>()

export type UploadFileSuccessMeta = {
  batchId: string
  scope: KnowledgeScope
  baseFolderId: number
  /** 文件实际落入的文件夹（含按相对路径自动创建的子目录） */
  targetFolderId: number
  /** 上传路径上需刷新的文件夹（含基目录与各层父级） */
  refreshFolderIds: number[]
  indexJobId?: string
}

const fileSuccessListeners = new Set<(meta: UploadFileSuccessMeta) => void>()

/** 单个文件上传成功（传输队列中每完成一个触发一次） */
export function subscribeUploadFileSuccess(listener: (meta: UploadFileSuccessMeta) => void) {
  fileSuccessListeners.add(listener)
  return () => {
    fileSuccessListeners.delete(listener)
  }
}

function emitUploadFileSuccess(
  task: UploadTask,
  targetFolderId: number,
  refreshFolderIds: number[],
  indexJobId?: string,
) {
  const batchMeta = batches.get(task.batchId)
  if (!batchMeta) return
  const payload: UploadFileSuccessMeta = {
    batchId: task.batchId,
    scope: batchMeta.scope,
    baseFolderId: batchMeta.baseFolderId,
    targetFolderId,
    refreshFolderIds,
    indexJobId,
  }
  fileSuccessListeners.forEach((fn) => fn(payload))
}

/** 任意批次上传结束（含右上角传输管理里完成的任务） */
export function subscribeUploadBatchComplete(listener: (meta: UploadBatchCompleteMeta) => void) {
  batchCompleteListeners.add(listener)
  return () => {
    batchCompleteListeners.delete(listener)
  }
}

function emit() {
  listeners.forEach((fn) => fn())
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function getSnapshot() {
  return tasks
}

export function useUploadQueue() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function getUploadBatches(): UploadBatchMeta[] {
  return [...batches.values()].sort((a, b) => b.createdAt - a.createdAt)
}

export function uploadQueueStats() {
  const active = tasks.filter((t) => t.status === 'pending' || t.status === 'uploading')
  const failed = tasks.filter((t) => t.status === 'error')
  const done = tasks.filter((t) => t.status === 'done')
  return { active: active.length, failed: failed.length, done: done.length, total: tasks.length }
}

export function isSupportedUploadFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() || ''
  return SUPPORTED_UPLOAD_EXTENSIONS.has(ext)
}

export function fileTaskId(file: File, relativePath?: string) {
  const key = normalizeUploadRelativePath(relativePath) || file.name
  return `${key}-${file.size}-${file.lastModified}`
}

/** 浏览器文件夹选择：Windows 为反斜杠，统一为 / 以便按层级建夹 */
export function normalizeUploadRelativePath(path?: string): string | undefined {
  if (!path?.trim()) return undefined
  return path.trim().replace(/\\/g, '/').replace(/^\/+/, '')
}

/** 仅取路径中的文件名（避免 Windows 目录上传把整条路径写进标题/原名） */
export function uploadLeafFileName(file: File, relativePath?: string): string {
  const path = normalizeUploadRelativePath(relativePath)
  if (path) {
    const parts = path.split('/').filter(Boolean)
    if (parts.length) return parts[parts.length - 1]!
  }
  const normalized = file.name.replace(/\\/g, '/')
  const parts = normalized.split('/').filter(Boolean)
  return parts[parts.length - 1] || file.name
}

/** 相对路径中的目录段（不含文件名） */
export function uploadDirPartsFromRelativePath(relativePath?: string): string[] {
  const normalized = normalizeUploadRelativePath(relativePath)
  if (!normalized?.includes('/')) return []
  const segments = normalized.split('/').filter(Boolean)
  if (segments.length <= 1) return []
  return segments.slice(0, -1)
}

function patchTask(id: string, patch: Partial<UploadTask>) {
  tasks = tasks.map((t) => (t.id === id ? { ...t, ...patch } : t))
  emit()
}

function nextPending(): UploadTask | undefined {
  return tasks.find((t) => t.status === 'pending')
}

async function listFolderChildren(
  parentId: number | null,
  scope: KnowledgeScope,
): Promise<FolderNode[]> {
  const params: Record<string, unknown> = { ...scopeQuery(scope) }
  if (parentId != null) params.parent_id = parentId
  const res = await adminApi.get<{ items: FolderNode[] }>(
    '/admin/knowledge/folders/children',
    {
      params,
    },
  )
  return res.data.items
}

type FolderPathCache = {
  ids: Map<string, number>
  inflight: Map<string, Promise<number>>
}

async function ensureFolderSegment(
  parentId: number,
  part: string,
  pathKey: string,
  scope: KnowledgeScope,
  cache: FolderPathCache,
): Promise<number> {
  const hit = cache.ids.get(pathKey)
  if (hit != null) return hit

  let pending = cache.inflight.get(pathKey)
  if (!pending) {
    pending = (async () => {
      const siblings = await listFolderChildren(parentId, scope)
      const existing = siblings.find((f) => f.name === part)
      if (existing) return existing.id
      const created = await adminApi.post<{ id: number }>(
        '/admin/knowledge/folders',
        { name: part, parent_id: parentId },
        { params: scopeQuery(scope) },
      )
      return created.data.id
    })()
    cache.inflight.set(pathKey, pending)
  }

  try {
    const folderId = await pending
    cache.ids.set(pathKey, folderId)
    return folderId
  } finally {
    if (cache.inflight.get(pathKey) === pending) cache.inflight.delete(pathKey)
  }
}

async function getOrCreateFolderPath(
  baseFolderId: number,
  dirParts: string[],
  scope: KnowledgeScope,
  cache: FolderPathCache,
): Promise<{ folderId: number; refreshFolderIds: number[] }> {
  let currentId = baseFolderId
  const refreshFolderIds = [baseFolderId]
  let pathKey = String(baseFolderId)
  for (const part of dirParts) {
    const name = part.trim()
    if (!name) continue
    pathKey = `${pathKey}/${name}`
    currentId = await ensureFolderSegment(currentId, name, pathKey, scope, cache)
    refreshFolderIds.push(currentId)
  }
  return { folderId: currentId, refreshFolderIds }
}

async function runTask(task: UploadTask, folderCache: FolderPathCache) {
  const opts = optionsByBatch.get(task.batchId)
  if (!opts) {
    patchTask(task.id, { status: 'error', error: '上传批次已失效', finishedAt: Date.now() })
    return
  }

  let folderId = task.targetFolderId
  let refreshFolderIds = [task.targetFolderId]
  const dirParts = uploadDirPartsFromRelativePath(task.relativePath)
  if (dirParts.length) {
    try {
      const resolved = await getOrCreateFolderPath(task.targetFolderId, dirParts, task.scope, folderCache)
      folderId = resolved.folderId
      refreshFolderIds = resolved.refreshFolderIds
    } catch (e) {
      patchTask(task.id, {
        status: 'error',
        error: formatApiError(e, '创建文件夹失败'),
        finishedAt: Date.now(),
      })
      return
    }
  }

  patchTask(task.id, { status: 'uploading', percent: 0, targetFolderId: folderId })

  const fd = new FormData()
  const leafName = uploadLeafFileName(task.file, task.relativePath)
  const stem = leafName.replace(/\.[^.]+$/, '') || leafName
  fd.append('title', stem)
  fd.append('folder_id', String(folderId))
  fd.append('category', opts.category || '文档')
  if (opts.entity_type?.trim()) {
    fd.append('entity_type', opts.entity_type.trim())
  }
  fd.append('tags', opts.tags || '')
  fd.append('use_ocr', opts.use_ocr ? 'true' : 'false')
  fd.append('auto_index', 'true')
  fd.append('scope', task.scope)
  fd.append('chunk_strategy', opts.chunk_strategy || 'fixed')
  fd.append('files', task.file, leafName)

  try {
    const res = await adminLongRunningApi.post<{ index_job_id?: string | null }>(
      '/admin/knowledge/items/upload',
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: scopeQuery(task.scope),
        onUploadProgress: (e) => {
          if (!e.total) return
          const pct = Math.round((e.loaded * 100) / e.total)
          patchTask(task.id, { status: 'uploading', percent: pct })
        },
      },
    )
    patchTask(task.id, { status: 'done', percent: 100, finishedAt: Date.now() })
    emitUploadFileSuccess(task, folderId, refreshFolderIds, res.data.index_job_id || undefined)
  } catch (e) {
    patchTask(task.id, {
      status: 'error',
      error: formatApiError(e, '上传失败'),
      finishedAt: Date.now(),
    })
  }
}

function maybeFinishBatch(batchId: string) {
  const batchTasks = tasks.filter((t) => t.batchId === batchId)
  if (!batchTasks.length) return
  const allDone = batchTasks.every((t) => t.status === 'done' || t.status === 'error')
  if (!allDone) return
  const batchMeta = batches.get(batchId)
  completeCallbacks.get(batchId)?.()
  completeCallbacks.delete(batchId)
  if (batchMeta) {
    const payload: UploadBatchCompleteMeta = {
      batchId,
      scope: batchMeta.scope,
      baseFolderId: batchMeta.baseFolderId,
      done: batchTasks.filter((t) => t.status === 'done').length,
      failed: batchTasks.filter((t) => t.status === 'error').length,
    }
    batchCompleteListeners.forEach((fn) => fn(payload))
  }
}

function newFolderPathCache(): FolderPathCache {
  return { ids: new Map(), inflight: new Map() }
}

async function pumpQueue(folderCache: FolderPathCache) {
  while (activeCount < MAX_CONCURRENT) {
    const task = nextPending()
    if (!task) break
    activeCount += 1
    patchTask(task.id, { status: 'uploading', percent: 0 })
    void runTask(task, folderCache).finally(() => {
      activeCount -= 1
      maybeFinishBatch(task.batchId)
      void pumpQueue(folderCache)
    })
  }
}

export type EnqueueFile = {
  file: File
  relativePath?: string
}

export function enqueueKnowledgeUpload(params: {
  scope: KnowledgeScope
  baseFolderId: number
  baseFolderLabel?: string
  files: EnqueueFile[]
  options: UploadOptions
  onBatchComplete?: () => void
}): string {
  const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const now = Date.now()
  const label =
    params.baseFolderLabel?.trim() ||
    (params.files.some((f) => f.relativePath) ? '文件夹上传' : '文档上传')

  batches.set(batchId, {
    id: batchId,
    scope: params.scope,
    baseFolderId: params.baseFolderId,
    label,
    createdAt: now,
  })
  optionsByBatch.set(batchId, params.options)
  if (params.onBatchComplete) completeCallbacks.set(batchId, params.onBatchComplete)

  const newTasks: UploadTask[] = params.files.map(({ file, relativePath }) => {
    const normalizedPath = normalizeUploadRelativePath(relativePath)
    return {
      id: fileTaskId(file, normalizedPath),
      batchId,
      batchLabel: label,
      file,
      displayName: normalizedPath || uploadLeafFileName(file, normalizedPath),
      relativePath: normalizedPath,
      targetFolderId: params.baseFolderId,
      scope: params.scope,
      status: 'pending' as const,
      percent: 0,
      createdAt: now,
    }
  })

  tasks = [...newTasks, ...tasks]
  emit()
  void pumpQueue(newFolderPathCache())
  return batchId
}

export function retryUploadTask(taskId: string) {
  const task = tasks.find((t) => t.id === taskId)
  if (!task || task.status !== 'error') return
  patchTask(taskId, { status: 'pending', percent: 0, error: undefined, finishedAt: undefined })
  void pumpQueue(newFolderPathCache())
}

export function retryFailedUploads(batchId?: string) {
  for (const task of tasks) {
    if (task.status !== 'error') continue
    if (batchId && task.batchId !== batchId) continue
    patchTask(task.id, { status: 'pending', percent: 0, error: undefined, finishedAt: undefined })
  }
  void pumpQueue(newFolderPathCache())
}

export function clearFinishedUploads() {
  tasks = tasks.filter((t) => t.status !== 'done')
  const batchIds = new Set(tasks.map((t) => t.batchId))
  for (const id of batches.keys()) {
    if (!batchIds.has(id)) {
      batches.delete(id)
      optionsByBatch.delete(id)
      completeCallbacks.delete(id)
    }
  }
  emit()
}

export function removeUploadTask(taskId: string) {
  const task = tasks.find((t) => t.id === taskId)
  if (!task || task.status === 'uploading') return
  tasks = tasks.filter((t) => t.id !== taskId)
  const batchIds = new Set(tasks.map((t) => t.batchId))
  if (!batchIds.has(task.batchId)) {
    batches.delete(task.batchId)
    optionsByBatch.delete(task.batchId)
    completeCallbacks.delete(task.batchId)
  }
  emit()
}
